import React, { useState } from "react";
import api from "../api";
import "../styles/Login.css";

const Login = ({ onLogin, onSetToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password || password.length < 4) {
      setError("Password must be at least 4 characters.");
      return;
    }

    try {
      const res = await api.post("v1/login", {
        email,
        password,
      });

      const payload = res.data?.data || res.data || {};

      if (payload.user?.is_admin) {
        setError("Admin users cannot log in to the tenant panel.");
        return;
      }
      const companies = Array.isArray(payload.companies)
        ? payload.companies
        : Array.isArray(payload.api_keys)
        ? payload.api_keys.map((k) => k.company || { api_key: k.api_key })
        : [];

      if (!Array.isArray(companies) || companies.length === 0) {
        setError("This account is not associated with any company.");
        return;
      }

      if (typeof onSetToken === "function" && payload.token)
        onSetToken(payload.token);
      if (typeof onLogin === "function") {
        onLogin(payload.user, companies, {
          token: payload.token,
        });
      }
    } catch (err) {
      let serverMessage =
        err.response?.data?.message || err.response?.data || err.message;

      if (err.response?.status === 401) {
        serverMessage = "Incorrect email or password. Please try again.";
      } else if (err.response?.status === 403) {
        serverMessage =
          err.response?.data?.message || "You are not allowed to login here.";
      }

      setError(serverMessage || "Login failed");
    }
  };

  return (
    <div className="login-wrapper full-login">
      <div className="brain-logo">
        <img src="/images/BrainLogo.png" alt="BrainSolutions"/>
      </div>
      <div className="login-container card card-primary redesigned-login">
        <div className="card-header">
          <h2 className="login-title">Login to Your Dashboard</h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="field" style={{ position: "relative" }}>
              {" "}
              <div className="password-header">
                <label htmlFor="email" className="login-label">
                  Email
                </label>
              </div>
              <div className="input-with-icon">
                <svg
                  className="input-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="login-input with-icon"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="field" style={{ position: "relative" }}>
              <div className="password-header">
                <label htmlFor="password" className="login-label">
                  Password
                </label>
              </div>
              <div className="input-with-icon">
                <svg
                  className="input-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <circle cx="12" cy="16" r="1" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="login-input with-icon"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: "36px" }}
                />
              </div>
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
                className="show-password-btn"
              >
                {showPassword ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5 0-9.27-3.11-10.94-7.5a10.05 10.05 0 0 1 1.73-2.82M1 1l22 22" />
                    <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-5.97" />
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12S5 5 12 5s11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            <button
              type="submit"
              className="btn btn-lg btn-primary btn-block login-btn"
            >
              Login
            </button>

            {error && (
              <div className="login-error redesigned-error">{error}</div>
            )}
          </form>
        </div>
      </div>

      <div className="login-footer">
        <p className="powered-by">
          <a
            href="https://www.braindevsolutions.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            Powered by Brain Solutions
          </a>
        </p>
        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            color: "#888",
            marginTop: "2px",
          }}
        >
          &copy; 2025 All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Login;
